import { BarsLegend } from '@components/graphComponents/BarsLegend';
import XAxis from '@components/graphComponents/DatedXAxis';
import GraphDataProvider, { GraphContext } from '@components/graphComponents/GraphDataProvider';
import { GroupedBars } from '@components/graphComponents/GroupedBars';
import { Alignments, Overlay } from '@components/graphComponents/Overlay';
import { DataEntry, GraphType, YAxis } from '@components/graphComponents/types';
import { HOVER_INFO_HEIGHT, HOVER_Y_OFFSET } from '@components/graphComponents/utils';
import { generateTicks, generateTicksFromSeconds, YAxisLines, YAxisTicks } from '@components/graphComponents/YAxis';
import { DieselAndElectric, Formatter, Section } from '@components/summary/types';
import SegmentableDay from '@features/ui/SegmentableDay';
import SegmentableDayOverlay from '@features/ui/SegmentableDayOverlay';
import { HoverInfo } from '@pages/driverAnalysis/types';
import { VehicleGraphDimensions } from '@pages/vehicleAnalysis/vehicleDataGraph';
import { formatSecondsToTime } from '@utils/stringFormatters';
import _ from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { FormattedMessage, IntlFormatters } from 'react-intl';

import { HOURS } from '../../constants/units';
import { DateRange } from '../../types';
import { BARS_INFO } from './utils';

const dieselAndElectricBarsSchema = (scaleName: YAxis) => {
    switch (scaleName) {
        case 'leftUpY':
            return BARS_INFO.ICE.colorValue;
        case 'rightUpY':
            return BARS_INFO.BEV_UP.colorValue;
        case 'rightDownY':
            return BARS_INFO.BEV_DOWN.colorValue;
        default:
            return '';
    }
};

const TwoUpAndOneDownBarsGraph = ({
    column,
    data,
    dateRange,
    dimensions,
    intl: { formatNumber = (value: number) => value.toString(), formatDate, formatMessage },
    languageData,
    tickFormatter,
}: {
    column: Section;
    data: DataEntry[];
    dateRange: DateRange;
    dimensions: VehicleGraphDimensions;
    intl: IntlFormatters;
    languageData: Record<string, string>;
    tickFormatter: (dateRange: DateRange) => (d: Date) => string;
}) => {
    const [hoverInfo, setHover] = useState<HoverInfo | null>(null);
    const [hoverInfoXOffset, setHoverInfoXOffset] = useState<number>(0);
    const [yZeroPosition, setYZeroPosition] = useState<number>(dimensions.height);
    const rightYTicks = useRef<number[]>([]);

    const leftYScaleFormatter = (column.formatter as DieselAndElectric<Formatter>).diesel;
    const leftYScaleUnit = (column.unit as DieselAndElectric<string>).diesel;

    const rightYScaleFormatter = (column.formatter as DieselAndElectric<Formatter>).electric;
    const rightYScaleUnit = (column.unit as DieselAndElectric<string>).electric;

    const hasLeftBarDataForHoveredDate = _.get(hoverInfo, 'data.leftUpY', 0) > 0;
    const hasRightUpBarDataForHoveredDate = _.get(hoverInfo, 'data.rightUpY', 0) > 0;
    const hasRightDownBarDataForHoveredDate = _.get(hoverInfo, 'data.rightDownY', 0) > 0;
    const hasNoDataForHoveredDate =
        !hasLeftBarDataForHoveredDate && !hasRightUpBarDataForHoveredDate && !hasRightDownBarDataForHoveredDate;
    const hasSomeDataForHoveredDate =
        hasLeftBarDataForHoveredDate || hasRightUpBarDataForHoveredDate || hasRightDownBarDataForHoveredDate;

    const saveRightTicks = useCallback((ticks: number[]) => (rightYTicks.current = ticks), []);

    const twoUpAndOneDownBarsInfo = {
        ICE: { label: _.get(languageData, 'consumption.fuel'), color: BARS_INFO.ICE.colorValue },
        BEV_UP: { label: _.get(languageData, 'consumption.electric'), color: BARS_INFO.BEV_UP.colorValue },
        BEV_DOWN: {
            label: _.get(languageData, 'consumption.electricRecuperation'),
            color: BARS_INFO.BEV_DOWN.colorValue,
        },
    };

    return (
        <div
            className="graph bg-white margin-bottom-1 position-relative overflow-hidden"
            style={{ height: dimensions.height + dimensions.legendHeight }}
            data-test="vehicle-two-up-and-one-down-bars-TruE-graph"
        >
            {hoverInfo && (
                <>
                    {hasNoDataForHoveredDate && (
                        <Overlay hasData x={hoverInfo.leftX + hoverInfoXOffset} y={HOVER_Y_OFFSET}>
                            <FormattedMessage id="noData" />
                        </Overlay>
                    )}
                    {hasSomeDataForHoveredDate && (
                        <>
                            {hasLeftBarDataForHoveredDate && (
                                <Overlay
                                    withDataClasses={`border-color-${BARS_INFO.ICE.colorName} text-color-${BARS_INFO.ICE.colorName} bg-white`}
                                    hasData
                                    maxXValue={dimensions.width}
                                    x={hoverInfo.leftX}
                                    y={
                                        hoverInfo.leftUpY + HOVER_INFO_HEIGHT > yZeroPosition
                                            ? hoverInfo.leftUpY - HOVER_INFO_HEIGHT
                                            : hoverInfo.leftUpY
                                    }
                                    alignment={Alignments.RIGHT}
                                >
                                    {leftYScaleFormatter(hoverInfo.data.leftUpY)}
                                </Overlay>
                            )}
                            {hasRightUpBarDataForHoveredDate && (
                                <Overlay
                                    hasData
                                    x={hoverInfo.rightX}
                                    y={
                                        hoverInfo.rightUpY + HOVER_INFO_HEIGHT > yZeroPosition
                                            ? hoverInfo.rightUpY - HOVER_INFO_HEIGHT
                                            : hoverInfo.rightUpY
                                    }
                                    maxXValue={dimensions.width}
                                    useCustomStyleColors
                                    style={{
                                        borderColor: BARS_INFO.BEV_UP.colorValue,
                                        color: BARS_INFO.BEV_UP.colorValue,
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    {rightYScaleFormatter(hoverInfo.data.rightUpY)}
                                </Overlay>
                            )}
                            {hasRightDownBarDataForHoveredDate && (
                                <Overlay
                                    withDataClasses={`border-color-${BARS_INFO.BEV_DOWN.colorName} text-color-${BARS_INFO.BEV_DOWN.colorName} bg-white`}
                                    hasData
                                    maxXValue={dimensions.width}
                                    x={hoverInfo.rightX}
                                    y={
                                        hoverInfo.rightDownY < yZeroPosition
                                            ? hoverInfo.rightDownY + HOVER_INFO_HEIGHT
                                            : hoverInfo.rightDownY
                                    }
                                >
                                    {rightYScaleFormatter(hoverInfo.data.rightDownY)}
                                </Overlay>
                            )}
                        </>
                    )}
                </>
            )}
            <GraphDataProvider
                formatDate={formatDate}
                formatMessage={formatMessage}
                dimensions={dimensions}
                data={data}
                hoverInfoXOffset={hoverInfoXOffset}
                setHoverInfoXOffset={setHoverInfoXOffset}
                setYZeroPosition={setYZeroPosition}
                yScaleShouldStartAtZero
                graphType={GraphType.TWO_UP_AND_ONE_DOWN_BARS}
                shouldRoundYScales
            >
                <SegmentableDayOverlay
                    leftUpYAlignment={Alignments.RIGHT}
                    leftUpYColor={BARS_INFO.ICE.colorName}
                    leftYScaleFormatter={leftYScaleFormatter}
                    rightUpYColor={BARS_INFO.BEV_UP.colorValue}
                    rightDownYColor="highlight"
                    rightYScaleFormatter={rightYScaleFormatter}
                    shouldShowRightUpYValue
                    shouldShowRightDownYValue
                    useOuterXPosition
                    xOffset={hoverInfoXOffset}
                />
                <svg
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMinYMin meet"
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height - dimensions.margin}`}
                >
                    <XAxis
                        tickFormatter={tickFormatter}
                        selectedElement={_.get(hoverInfo, 'data')}
                        dateRange={dateRange}
                        shouldHideTickLine
                    />
                    <YAxisLines
                        tickGenerator={rightYScaleUnit === HOURS ? generateTicksFromSeconds : generateTicks}
                        yScale="rightYScale"
                    />
                    <YAxisTicks
                        formatter={rightYScaleUnit === HOURS ? formatSecondsToTime : formatNumber}
                        tickGenerator={rightYScaleUnit === HOURS ? generateTicksFromSeconds : generateTicks}
                        units={rightYScaleUnit ? `(${_.get(languageData, `unit.${rightYScaleUnit}`)})` : undefined}
                        unitsColor={BARS_INFO.BEV_UP.colorValue}
                        position="rightY"
                        saveTicks={saveRightTicks}
                    />
                    <YAxisTicks
                        formatter={leftYScaleUnit === HOURS ? formatSecondsToTime : formatNumber}
                        tickGenerator={leftYScaleUnit === HOURS ? generateTicksFromSeconds : generateTicks}
                        units={leftYScaleUnit ? `(${_.get(languageData, `unit.${leftYScaleUnit}`)})` : undefined}
                        unitsColor={BARS_INFO.ICE.colorValue}
                        customTicksYValues={rightYTicks.current}
                    />
                    <GraphContext.Consumer>
                        {({ setHoverInfoXOffset }) => (
                            <GroupedBars
                                colorSchema={dieselAndElectricBarsSchema}
                                minHeight={0}
                                setHoverInfoXOffset={setHoverInfoXOffset}
                            />
                        )}
                    </GraphContext.Consumer>
                    <SegmentableDay onHover={setHover} useOuterXPosition xOffset={hoverInfoXOffset} />
                    <BarsLegend barsInfo={twoUpAndOneDownBarsInfo} />
                </svg>
            </GraphDataProvider>
        </div>
    );
};

export default TwoUpAndOneDownBarsGraph;
